import { useMutation, useQuery } from "@tanstack/react-query";
import httpClient from "http-client/http-client";
import { useEffect } from "react";

export const useApiQuery = ({
  queryKey,
  url,
  method = "GET",
  params = {},
  headers = {},
  enabled = true,
  select,
  retry = false,
  staleTimeInMinutes = 0,
  onSuccess,
  onError,
}) => {
  const actualQueryKey = [queryKey, method, params];

  const query = useQuery({
    queryKey: actualQueryKey,
    queryFn: async () => {
      const httpMethod = method.toLowerCase();

      if (!httpClient[httpMethod]) {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }

      const response = await httpClient[httpMethod]({
        url,
        headers,
        params,
      });

      const ok =
        typeof response.statusCode === "number" &&
        response.statusCode >= 200 &&
        response.statusCode < 300;
      if (!ok) {
        throw response;
      }

      return response;
    },
    enabled,
    select,
    retry,
    staleTime: staleTimeInMinutes * 60 * 1000,
  });

  // Call user-defined onSuccess when query becomes successful
  useEffect(() => {
    if (query.isSuccess) {
      onSuccess?.(query.data?.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]);

  // Call user-defined onError when query fails
  useEffect(() => {
    if (query.isError) {
      onError?.(query.error?.errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    ...query,
    data: query.data?.data,
    errors: query.error?.errors,
  };
};

export const useApiMutation = ({
  url,
  method = "POST",
  headers = {},
  params = {},
  onSuccess,
  onError,
}) => {
  const mutation = useMutation({
    mutationFn: async (body) => {
      const httpMethod = method.toLowerCase();
      if (!httpClient[httpMethod]) {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }

      const response = await httpClient[httpMethod]({
        url,
        body,
        headers,
        params,
      });

      const ok =
        typeof response.statusCode === "number" &&
        response.statusCode >= 200 &&
        response.statusCode < 300;
      if (!ok) {
        throw response;
      }

      return response;
    },
    onSuccess: (data, variables) => {
      if (onSuccess) {
        onSuccess(data.data, variables);
      }
    },
    onError,
  });

  return mutation;
};
